import { useId } from 'react'
import { Field, Form } from 'react-final-form'
import { Button, CheckboxField, InputField } from '@mc/design-system'
import { Trans, useTranslation } from '@mc/i18n'
import { useFireEvent } from 'pages/certificates/tracking'
import {
  validateEmailWithGdpr,
  withoutFieldErrors,
} from 'pages/certificates/validations'
import { submitSyllabusRequest } from '../submitSyllabusRequest'
import type { CohortContent } from '../types'
import { useRememberedSubmission } from '../useRememberedSubmission'

type FormValues = {
  email: string
  gdprOptIn: boolean
}

type CohortSyllabusCaptureProps = {
  slug: string
  syllabusCapture: CohortContent['syllabusCapture']
}

export const CohortSyllabusCapture = ({
  slug,
  syllabusCapture,
}: CohortSyllabusCaptureProps) => {
  const { t } = useTranslation('@mc/certificates')
  const headingId = useId()
  const fireEvent = useFireEvent()
  const { status, headingRef, formRef, submitEmail, showForm } =
    useRememberedSubmission({
      submission: 'syllabus',
      slug,
      emailFieldName: 'email',
      request: submitSyllabusRequest,
      failureMessage: 'Cohort syllabus request failed',
    })

  const handleSubmit = ({ email, gdprOptIn }: FormValues) =>
    submitEmail(email, () =>
      fireEvent('certificateEmailCaptured', {
        action: syllabusCapture.submitLabel,
        location: 'syllabus',
        email,
        campaign: slug,
        gdpr_opt_in: gdprOptIn,
      }),
    )

  return (
    <section aria-labelledby={headingId} className='py-8 lg:container'>
      <div className='flex flex-col gap-6 rounded-lg bg-c-brand-highlight px-5 py-6 text-c-text-dark lg:flex-row lg:items-end lg:justify-between lg:gap-10 lg:px-8 lg:py-7'>
        <div className='flex flex-col gap-3 lg:w-5/12'>
          <h2
            id={headingId}
            ref={headingRef}
            tabIndex={-1}
            className='c-heading-1 m-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
          >
            {status.kind === 'success'
              ? t('marketingPages.cohorts.syllabus.successTitle')
              : syllabusCapture.title}
          </h2>
          <p className='c-body m-0'>
            {status.kind === 'success' ? (
              <Trans
                i18nKey='marketingPages.cohorts.syllabus.successBody'
                ns='@mc/certificates'
                values={{ email: status.email }}
                components={{ strong: <strong key='email' /> }}
              />
            ) : (
              syllabusCapture.description
            )}
          </p>
        </div>

        {status.kind === 'alreadyRequested' && (
          <div role='status' className='flex flex-col gap-3 lg:w-5/12'>
            <p className='c-body-bold m-0'>
              {t('marketingPages.cohorts.syllabus.alreadyRequestedTitle')}
            </p>
            <p className='c-body m-0'>
              {t('marketingPages.cohorts.syllabus.alreadyRequestedBody')}
            </p>
            <button
              type='button'
              className='c-body-bold cursor-pointer self-start border-0 bg-transparent p-0 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
              onClick={showForm}
            >
              {t('marketingPages.cohorts.syllabus.useDifferentEmail')}
            </button>
          </div>
        )}

        {(status.kind === 'idle' || status.kind === 'error') && (
          <Form<FormValues>
            validate={validateEmailWithGdpr}
            initialValues={{ gdprOptIn: false }}
            onSubmit={handleSubmit}
            render={({
              handleSubmit: submit,
              hasValidationErrors,
              submitting,
            }) => (
              <form
                ref={formRef}
                noValidate
                className='flex flex-col gap-3 lg:w-5/12'
                onSubmit={submit}
              >
                <div className='flex flex-col gap-3 lg:flex-row lg:items-end'>
                  <Field
                    name='email'
                    id='email'
                    type='email'
                    placeholder={syllabusCapture.emailPlaceholder}
                    autoComplete='email'
                    required
                    render={({ input, meta, ...rest }) => (
                      <InputField
                        {...rest}
                        input={input}
                        meta={withoutFieldErrors(meta)}
                        label={t('downloadSyllabus.emailAddress')}
                        className='flex-1'
                      />
                    )}
                  />
                  <Button
                    type='submit'
                    kind='primary'
                    disabled={hasValidationErrors}
                    loading={submitting}
                  >
                    {syllabusCapture.submitLabel}
                  </Button>
                </div>
                <Field
                  name='gdprOptIn'
                  label={syllabusCapture.consentLabel}
                  required
                  render={({ input, meta, ...rest }) => (
                    <CheckboxField
                      {...rest}
                      input={input}
                      meta={withoutFieldErrors(meta)}
                      className='c-body-xsmall'
                    />
                  )}
                />
                {status.kind === 'error' && (
                  <p role='alert' className='c-body-small m-0'>
                    {t('marketingPages.cohorts.syllabus.error')}
                  </p>
                )}
              </form>
            )}
          />
        )}
      </div>
    </section>
  )
}
